import type { IChatPlatform } from "@/src/screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";
import type { ChatMessage } from "@/src/services/eventBus/types";

type Listener = () => void;
type Simulation = {
  messages: ChatMessage[];
  timer: ReturnType<typeof setInterval>;
};

/** Ephemeral, per-widget preview. Never enters the live chat bus or storage. */
class ChatSimulation {
  private sessions = new Map<string, Simulation>();
  private listeners = new Map<string, Set<Listener>>();

  getSnapshot = (widgetId: string): ChatMessage[] | null =>
    this.sessions.get(widgetId)?.messages ?? null;

  subscribe(widgetId: string, listener: Listener): () => void {
    const listeners = this.listeners.get(widgetId) ?? new Set<Listener>();
    listeners.add(listener);
    this.listeners.set(widgetId, listeners);
    return () => {
      listeners.delete(listener);
      if (!listeners.size) this.listeners.delete(widgetId);
    };
  }

  private notify(widgetId: string): void {
    this.listeners.get(widgetId)?.forEach((listener) => listener());
  }

  start(widgetId: string, texts: string[], platforms?: IChatPlatform[]): void {
    this.stop(widgetId);
    if (!texts.length) return;
    const authors = ["Luna", "Alex", "Mika", "Sam", "Noa", "Dani"];
    const colors = ["#a78bfa", "#38bdf8", "#4ade80", "#fb923c"];
    const sources = platforms?.length ? platforms : ["twitch", "youtube", "kick"];
    let sequence = 0;
    const nextMessage = (): ChatMessage => {
      const index = sequence++;
      return {
        id: `chat-simulation:${widgetId}:${index}`,
        platform: sources[index % sources.length],
        authorId: `sample-${index % authors.length}`,
        authorName: authors[index % authors.length],
        authorBadges: [],
        authorColor: colors[index % colors.length],
        text: texts[index % texts.length],
        timestamp: Date.now(),
      };
    };
    const session: Simulation = {
      messages: [nextMessage()],
      timer: setInterval(() => {
        session.messages = [...session.messages, nextMessage()].slice(-30);
        this.notify(widgetId);
      }, 1200),
    };
    this.sessions.set(widgetId, session);
    this.notify(widgetId);
  }

  stop(widgetId: string): void {
    const session = this.sessions.get(widgetId);
    if (!session) return;
    clearInterval(session.timer);
    this.sessions.delete(widgetId);
    this.notify(widgetId);
  }
}

export const chatSimulation = new ChatSimulation();

export const isSimulatedChatMessage = (id: string): boolean =>
  id.startsWith("chat-simulation:");
